/**
 * "Giới thiệu bạn bè" (account menu): this account's link with "Sao chép" and, where the browser has a share sheet
 * (phones: Zalo, Messenger…), "Chia sẻ"; how many friends signed up through it and the tokens received. The rules
 * come from the server (GET /api/referrals); no Zalo / Facebook SDK.
 */
import { useEffect, useId, useRef, useState } from "react";
import { ApiError, type Api, type Referrals } from "../api";
import { REFERRAL, errorMessage } from "../messages";
import { referralUrl } from "../referral";
import { Modal } from "./Modal";

interface Props {
  api: Api;
  onClose: () => void;
}

async function copyText(text: string, field: HTMLInputElement | null): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // no Clipboard API (http, older browser): select the field so the user can copy it by hand
    field?.select();
    return false;
  }
}

export function ReferralDialog({ api, onClose }: Props) {
  const titleId = useId();
  const linkId = useId();
  const [data, setData] = useState<Referrals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; ok: boolean } | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let live = true;
    api.referrals().then((r) => live && setData(r))
      .catch((e) => live && setError(e instanceof ApiError ? e.code : "network"));
    return () => {
      live = false;
    };
  }, [api]);

  const url = data?.link ? referralUrl(data.link) : null;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function copy() {
    if (!url) return;
    const ok = await copyText(url, field.current);
    setNote({ text: ok ? REFERRAL.copied : REFERRAL.copyFailed, ok });
  }

  async function share() {
    if (!url) return;
    try {
      await navigator.share({ title: REFERRAL.shareTitle, text: REFERRAL.shareText, url });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return; // the user closed the share sheet
      console.warn("share failed", e);
      await copy();
    }
  }

  return (
    <Modal titleId={titleId} onClose={onClose} initialFocus={closeRef}>
      <h2 id={titleId}>{REFERRAL.title}</h2>
      {!data && !error && <p aria-live="polite">{REFERRAL.loading}</p>}
      {error && <p role="alert" className="error">{errorMessage(error)}</p>}
      {data && !data.enabled && <p>{REFERRAL.off}</p>}
      {data?.enabled && url && (
        <>
          <p>{REFERRAL.intro(data.reward)}</p>
          <div className="field">
            <label htmlFor={linkId}>{REFERRAL.linkLabel}</label>
            <input ref={field} id={linkId} className="text-input" value={url} readOnly
                   onFocus={(e) => e.currentTarget.select()} />
          </div>
          <div className="actions">
            <button type="button" className="btn btn-primary" onClick={() => void copy()}>{REFERRAL.copy}</button>
            {canShare && <button type="button" className="btn" onClick={() => void share()}>{REFERRAL.share}</button>}
          </div>
          {note && <p role={note.ok ? "status" : "alert"} className={note.ok ? "ok" : "error"}>{note.text}</p>}
          <dl className="wallets">
            <div><dt>{REFERRAL.invited}</dt><dd>{data.invited}</dd></div>
            <div><dt>{REFERRAL.rewarded}</dt><dd>{data.rewarded}</dd></div>
            <div><dt>{REFERRAL.tokens}</dt><dd>{data.tokens_earned}</dd></div>
          </dl>
          <p className="hint">
            {REFERRAL.thisMonth(data.rewarded_this_month, data.max_per_month)} {REFERRAL.rules(data.max_per_month, data.earned_cap)}
          </p>
        </>
      )}
      <div className="actions actions-end">
        <button ref={closeRef} type="button" className="btn btn-quiet" onClick={onClose}>{REFERRAL.close}</button>
      </div>
    </Modal>
  );
}
